/**
 * Buyer identity — optional OTP profile ("a key, not a lead").
 *
 * Sits OUTSIDE AdvisorProvider: signing in never gates the thread. What a
 * profile adds is persistence — the localStorage session blob syncs to the
 * buyer's Desk profile so the board follows them across devices.
 *
 * Sync rules:
 *   - every savePersistedState dispatches `naya:state-saved`; when signed
 *     in we debounce 2.5s and PUT the raw blob (newest-wins on savedAt)
 *   - on mount with a token: if the server blob is meaningfully newer than
 *     local, adopt it wholesale and reload once (guarded per tab)
 *   - on fresh sign-in: local state wins (pushed up); a blank device
 *     adopts the server blob instead
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  deleteProfile as apiDeleteProfile,
  fetchMe,
  fetchState,
  pushState,
  signOut as apiSignOut,
  startSignIn as apiStartSignIn,
  verifyCode as apiVerifyCode,
  type BuyerInfo,
  type BuyerMethod,
  type StartResponse,
} from '@/lib/buyer-auth-api';
import { clearAdvisorSession, readRawPersistedState, writeRawPersistedState } from '@/lib/session';

const TOKEN_KEY = 'naya_advisor_buyer_token';
const PULL_GUARD_KEY = 'naya_advisor_state_pulled';
/** Server must be newer than local by this margin before we overwrite local. */
const ADOPT_MARGIN_MS = 2000;

export type IdentitySheet = 'none' | 'signin' | 'profile';

interface BuyerAuthState {
  buyer: BuyerInfo | null;
  /** Auth bootstrap finished (token checked or absent). */
  ready: boolean;
  identitySheet: IdentitySheet;
  openSignIn: () => void;
  openProfile: () => void;
  closeIdentity: () => void;
  startSignIn: (method: BuyerMethod, identifier: string) => Promise<StartResponse>;
  verifyCode: (challengeId: string, code: string) => Promise<{ ok: boolean; error?: string }>;
  signOut: () => Promise<void>;
  deleteProfile: () => Promise<{ ok: boolean; error?: string }>;
  /** Last successful profile-sync timestamp (0 = never in this tab). */
  lastSyncedAt: number;
}

const BuyerAuthContext = createContext<BuyerAuthState | null>(null);

function readToken(): string {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem(TOKEN_KEY) ?? '';
}

export function BuyerAuthProvider({ children }: { children: React.ReactNode }) {
  const [buyer, setBuyer] = useState<BuyerInfo | null>(null);
  const [ready, setReady] = useState(false);
  const [identitySheet, setIdentitySheet] = useState<IdentitySheet>('none');
  const [lastSyncedAt, setLastSyncedAt] = useState(0);
  const tokenRef = useRef(readToken());
  const pushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setToken = useCallback((token: string) => {
    tokenRef.current = token;
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  }, []);

  const pushLocalState = useCallback(async () => {
    const token = tokenRef.current;
    if (!token) return;
    const local = readRawPersistedState();
    if (!local) return;
    const res = await pushState(token, local.raw, local.savedAt);
    if (res.ok) setLastSyncedAt(Date.now());
  }, []);

  // Bootstrap: validate token, then decide adopt-server vs keep-local.
  useEffect(() => {
    const token = tokenRef.current;
    if (!token) {
      setReady(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      const me = await fetchMe(token);
      if (cancelled) return;
      if (me.status !== 'ok' || !me.buyer) {
        setToken('');
        setReady(true);
        return;
      }
      setBuyer(me.buyer);
      const local = readRawPersistedState();
      const localSavedAt = local?.savedAt ?? 0;
      const serverSavedAt = me.state_saved_at ?? 0;
      const alreadyPulled = sessionStorage.getItem(PULL_GUARD_KEY) === '1';
      if (serverSavedAt > localSavedAt + ADOPT_MARGIN_MS && !alreadyPulled) {
        const remote = await fetchState(token);
        if (!cancelled && remote.status === 'ok' && typeof remote.state === 'string') {
          if (writeRawPersistedState(remote.state)) {
            sessionStorage.setItem(PULL_GUARD_KEY, '1');
            window.location.reload();
            return;
          }
        }
      }
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [setToken]);

  // Debounced push on every local save while signed in.
  useEffect(() => {
    const onSaved = () => {
      if (!tokenRef.current) return;
      if (pushTimerRef.current) clearTimeout(pushTimerRef.current);
      pushTimerRef.current = setTimeout(() => {
        void pushLocalState();
      }, 2500);
    };
    window.addEventListener('naya:state-saved', onSaved);
    return () => {
      window.removeEventListener('naya:state-saved', onSaved);
      if (pushTimerRef.current) clearTimeout(pushTimerRef.current);
    };
  }, [pushLocalState]);

  const startSignIn = useCallback(
    (method: BuyerMethod, identifier: string) => apiStartSignIn(method, identifier),
    [],
  );

  const verifyCode = useCallback(
    async (challengeId: string, code: string): Promise<{ ok: boolean; error?: string }> => {
      const res = await apiVerifyCode(challengeId, code);
      if (res.status !== 'ok' || !res.token || !res.buyer) {
        return { ok: false, error: res.error ?? 'verify_failed' };
      }
      setToken(res.token);
      setBuyer(res.buyer);
      // Claim-time merge: a device with a real session wins; a blank device
      // adopts the profile's saved board.
      const local = readRawPersistedState();
      const localMeaningful = (local?.savedAt ?? 0) > 0;
      if (localMeaningful) {
        void pushLocalState();
      } else if (typeof res.state === 'string' && res.state) {
        if (writeRawPersistedState(res.state)) {
          sessionStorage.setItem(PULL_GUARD_KEY, '1');
          window.location.reload();
        }
      }
      return { ok: true };
    },
    [pushLocalState, setToken],
  );

  const signOut = useCallback(async () => {
    const token = tokenRef.current;
    setToken('');
    setBuyer(null);
    setIdentitySheet('none');
    if (token) await apiSignOut(token).catch(() => undefined);
  }, [setToken]);

  const deleteProfile = useCallback(async (): Promise<{ ok: boolean; error?: string }> => {
    const token = tokenRef.current;
    if (!token) return { ok: false, error: 'not_signed_in' };
    const res = await apiDeleteProfile(token);
    if (res.status !== 'ok') return { ok: false, error: res.error ?? 'delete_failed' };
    // Erasure is total: key, sessions, synced board — and this device's copy.
    setToken('');
    setBuyer(null);
    clearAdvisorSession();
    sessionStorage.removeItem(PULL_GUARD_KEY);
    return { ok: true };
  }, [setToken]);

  const value = useMemo(
    () => ({
      buyer,
      ready,
      identitySheet,
      openSignIn: () => setIdentitySheet('signin'),
      openProfile: () => setIdentitySheet(buyer ? 'profile' : 'signin'),
      closeIdentity: () => setIdentitySheet('none'),
      startSignIn,
      verifyCode,
      signOut,
      deleteProfile,
      lastSyncedAt,
    }),
    [buyer, ready, identitySheet, startSignIn, verifyCode, signOut, deleteProfile, lastSyncedAt],
  );

  return <BuyerAuthContext.Provider value={value}>{children}</BuyerAuthContext.Provider>;
}

export function useBuyerAuth(): BuyerAuthState {
  const ctx = useContext(BuyerAuthContext);
  if (!ctx) throw new Error('useBuyerAuth must be used within BuyerAuthProvider');
  return ctx;
}
